import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Field } from 'react-final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { KnmResultItemSubjectTypesEnum } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultItemSubjectInfo';
import { IKnmOrder } from 'APIPackage/KnmModule/interfaces/IKnmOrder';

import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { makeIsInn } from '../../../CorePackage/MainModule/validators/isInn';
import { makeIsOgrn } from '../../../CorePackage/MainModule/validators/isOgrn';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { knmInfoGetIsOrgInSonkoRegistry } from '../actions/knmActions';
import { IKnmForm } from '../interfaces/IKnmForm';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { getKnmDataCreated, getSonkoRegistryData } from '../selectors/knmSelectors';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { IReasonRequirements } from '../../../APIPackage/DictionariesModule/interfaces/IReasonRequirements';
import { CHECKING_IN_REGESTRY, CHECKING_IN_REGESTRY_ORDER } from '../../../CorePackage/MainModule/constants/notices';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';

const getSonkoKey = (ogrn: string | undefined, inn: string | undefined): string => {
  return `${ogrn || ''}|${inn || ''}`;
};

const sonkoValueToText = (value: boolean | undefined): string => {
  if (value === undefined) return '';

  return value ? 'Да' : 'Нет';
};

interface IKnmSonkoFieldProps {
  checkByInnMessage?: string;
  formRef: IKnmFormRef | undefined;
  id?: string;
  inn?: string;
  inspectionResult?: IInspectionResult;
  isShowMessages?: boolean;
  name?: string;
  ogrn?: string;
  subjectType?: IDictionarySelectOption<ITargetEntity>;
  value?: boolean;
}

interface IKnmSonkoFieldStoreProps {
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
  sonkoRegistryData?: { [key: string]: boolean };
}

const KnmSonkoFieldWithoutStore = ({
  id,
  inn,
  inspectionResult,
  isShowMessages,
  formRef,
  name,
  ogrn,
  subjectType,
  value,
  created,
  settings,
  sonkoRegistryData,
  checkByInnMessage,
}: IKnmSonkoFieldProps & IKnmSonkoFieldStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const isOrgInSonkoRegistry = sonkoRegistryData?.[getSonkoKey(ogrn, inn)];
  const [isShowValue, setIsShowValue] = useState(isOrgInSonkoRegistry);
  const [sonkoText, setSonkoText] = useState(sonkoValueToText(value));

  const isShowMessage4154 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_54_STARTS_FROM,
  );

  const isShowMessage5234153 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const isOgrnFilled = useCallback((): boolean => {
    return !!ogrn && makeIsOgrn('')(ogrn) === undefined;
  }, [ogrn]);

  const isInnFilled = useCallback((): boolean => {
    return !!inn && makeIsInn('')(inn) === undefined;
  }, [inn]);

  const isFiz = subjectType?.label === KnmSubjectTypesEnum.FIZ;

  /** Пока Redux не обновился, эффект может сработать много раз (нестабильные deps) — не дублируем один и тот же запрос. */
  const sonkoFetchRequestedForKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (isShowValue) {
      if (typeof isOrgInSonkoRegistry === 'boolean') {
        formRef?.current.change(name as keyof IKnmForm, isOrgInSonkoRegistry);
      }
      setSonkoText(sonkoValueToText(isOrgInSonkoRegistry));
    }
  }, [isOrgInSonkoRegistry, isShowValue, formRef, name]);

  useEffect(() => {
    const typeLabel = subjectType?.label;

    const isUR = subjectType?.code === KnmResultItemSubjectTypesEnum.UR;
    const isIP = subjectType?.code === KnmResultItemSubjectTypesEnum.IP;
    const isIUR = subjectType?.code === KnmResultItemSubjectTypesEnum.IUR;
    const isFIUR = subjectType?.code === KnmResultItemSubjectTypesEnum.FIUR;

    if (
      (isUR ||
        isIP ||
        isIUR ||
        isFIUR ||
        typeLabel === KnmSubjectTypesEnum.UR ||
        typeLabel === KnmSubjectTypesEnum.IP ||
        typeLabel === KnmSubjectTypesEnum.IUR ||
        typeLabel === KnmSubjectTypesEnum.FIUR) &&
      (isOgrnFilled() || isInnFilled())
    ) {
      setIsShowValue(true);
      const sonkoKey = getSonkoKey(ogrn, inn);

      if (isOrgInSonkoRegistry === undefined) {
        if (sonkoFetchRequestedForKeyRef.current !== sonkoKey) {
          sonkoFetchRequestedForKeyRef.current = sonkoKey;
          dispatch(knmInfoGetIsOrgInSonkoRegistry(ogrn, inn));
        }
      } else {
        sonkoFetchRequestedForKeyRef.current = null;
      }
    } else {
      sonkoFetchRequestedForKeyRef.current = null;
      setIsShowValue(false);
      name && formRef?.current.change(name as keyof IKnmForm);
    }
  }, [subjectType, formRef, name, isOrgInSonkoRegistry, inn, ogrn, dispatch, isOgrnFilled, isInnFilled]);

  const reasonRequirements: IReasonRequirements | undefined = formRef?.current.getFieldState(`reasonRequirements`)
    ?.value;
  const orderRequirements: IKnmOrder | undefined = formRef?.current.getFieldState(`orderRequirements`)?.value;

  /**
   * Ошибка валидации СОНКО
   */
  const isErrorValidationSonkoCompany = useMemo(() => {
    if (orderRequirements && isShowMessage5234153) {
      if (orderRequirements.flagSonko !== null) {
        if (
          value !== undefined &&
          ((orderRequirements.flagSonko === 'true' && !value) || (orderRequirements.flagSonko === 'false' && value))
        ) {
          return CHECKING_IN_REGESTRY_ORDER;
        }
      }
    } else if (reasonRequirements && reasonRequirements.sonko !== null && isShowMessage4154) {
      if (
        value !== undefined &&
        ((reasonRequirements.sonko && !value) || (reasonRequirements?.sonko === false && value))
      ) {
        return CHECKING_IN_REGESTRY;
      }
    }

    return '';
  }, [reasonRequirements, value]);

  return (
    <>
      {subjectType && !isFiz && (
        <>
          <FieldBlock
            label="Наличие проверяемого лица в реестре СОНКО"
            placementLabel="left"
            margin="top"
            id={id}
            warning={isShowMessages ? checkByInnMessage?.replace('ФНС', 'СОНКО') : undefined}
            error={isErrorValidationSonkoCompany}
          >
            <TextInput
              className="shared-row-field"
              value={sonkoText}
              disabled
              inspectionResult={inspectionResult}
              name={`${name}` || ''}
            />
          </FieldBlock>
          <Field name={`${name}` || ''}>{() => null}</Field>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmSonkoFieldStoreProps => ({
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
  sonkoRegistryData: getSonkoRegistryData(state),
});

export const KnmSonkoField = connect(mapStateToProps)(KnmSonkoFieldWithoutStore);
